// Game Engine — Core loop for Focus Catcher
// Manages targets, particles, scoring, combo, waves, and DDA.
// Uses requestAnimationFrame for smooth 60fps rendering.

import { getLevel, getWaveMultiplier, getCurrentWave, isLevelComplete, getEndlessDifficultyScale, TOTAL_GAMEPLAY_TIME } from './gameLevels';
import gameAudio from './gameAudio';

// ─── Constants ──────────────────────────────────────────────────────────────

const TARGET_TYPE_STANDARD = 'standard';
const TARGET_TYPE_DECOY = 'decoy';
const TARGET_TYPE_GOLDEN = 'golden';
const TARGET_TYPE_MOVING = 'moving';

const SPEED_THRESHOLDS = {
    PERFECT: 0.5,
    FAST: 1.0,
    GOOD: 1.5,
};

const COMBO_MULTIPLIERS = [
    { min: 30, mult: 8 },
    { min: 20, mult: 5 },
    { min: 10, mult: 3 },
    { min: 5, mult: 2 },
    { min: 0, mult: 1 },
];

const COMBO_MILESTONES = [5, 10, 25, 50, 100];

// ─── Particle Pool ──────────────────────────────────────────────────────────

function createParticlePool(size) {
    const pool = [];
    for (let i = 0; i < size; i++) {
        pool.push({
            active: false, x: 0, y: 0,
            vx: 0, vy: 0, life: 0, maxLife: 0,
            size: 0, color: '#00E5FF', alpha: 1,
        });
    }
    return pool;
}

function spawnParticles(pool, x, y, count, color) {
    let spawned = 0;
    for (let i = 0; i < pool.length && spawned < count; i++) {
        if (!pool[i].active) {
            const angle = (Math.PI * 2 * spawned) / count + (Math.random() - 0.5) * 0.5;
            const speed = 80 + Math.random() * 120;
            pool[i].active = true;
            pool[i].x = x;
            pool[i].y = y;
            pool[i].vx = Math.cos(angle) * speed;
            pool[i].vy = Math.sin(angle) * speed - 30; // slight upward bias
            pool[i].life = 0;
            pool[i].maxLife = 0.3 + Math.random() * 0.15;
            pool[i].size = 2 + Math.random() * 3;
            pool[i].color = color || '#00E5FF';
            pool[i].alpha = 1;
            spawned++;
        }
    }
}

function updateParticles(pool, dt) {
    for (let i = 0; i < pool.length; i++) {
        const p = pool[i];
        if (!p.active) continue;
        p.life += dt;
        if (p.life >= p.maxLife) {
            p.active = false;
            continue;
        }
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vy += 200 * dt; // gravity
        p.alpha = 1 - (p.life / p.maxLife);
    }
}

// ─── Score Popups ───────────────────────────────────────────────────────────

function createPopup(x, y, text, color) {
    return { x, y, text, color, life: 0, maxLife: 0.6, alpha: 1 };
}

function updatePopups(popups, dt) {
    for (let i = popups.length - 1; i >= 0; i--) {
        const p = popups[i];
        p.life += dt;
        p.y -= 60 * dt; // float upward
        p.alpha = 1 - (p.life / p.maxLife);
        if (p.life >= p.maxLife) {
            popups.splice(i, 1);
        }
    }
}

// ─── Game Engine ────────────────────────────────────────────────────────────

class GameEngine {
    constructor() {
        this.reset();
    }

    reset() {
        this.targets = [];
        this.particles = createParticlePool(60);
        this.popups = [];
        this.score = 0;
        this.combo = 0;
        this.bestCombo = 0;
        this.totalHits = 0;
        this.totalMisses = 0;
        this.totalDecoyTaps = 0;
        this.reactionTimes = [];
        this.elapsedTime = 0;
        this.lastSpawnTime = 0;
        this.level = null;
        this.paused = false;
        this.gameOver = false;
        this.ddaModifier = 1.0;
        this.recentHits = 0;
        this.recentMisses = 0;
        this.recentWindow = [];
        this.tutorialHitCount = 0;
        this.screenFlash = 0;
        this.comboMilestoneHit = null;
        this._decoySpawned = false;
        this._goldenSpawned = false;
        this._movingSpawned = false;
        this.goldenHits = 0;
    }

    startLevel(levelId) {
        this.reset();
        this.level = getLevel(levelId);
    }

    getBaseTargetSize(viewportWidth, viewportHeight) {
        const base = Math.min(viewportWidth, viewportHeight) * 0.12;
        return Math.max(24, Math.min(80, base * this.level.targetSize));
    }

    getHitZoneSize(visualSize) {
        if (visualSize >= 60) return visualSize * 1.25;
        if (visualSize >= 45) return visualSize * 1.4;
        if (visualSize >= 30) return visualSize * 1.6;
        return Math.max(visualSize * 2.0, 44);
    }

    _getDifficultyMult() {
        if (this.level.isEndless) return getEndlessDifficultyScale(this.elapsedTime);
        return getWaveMultiplier(this.elapsedTime);
    }

    _getEffectiveDuration() {
        const mult = this._getDifficultyMult();
        return this.level.duration / (mult * this.ddaModifier);
    }

    _getEffectiveSpawnRate() {
        const mult = this._getDifficultyMult();
        return this.level.spawnRate / (mult * this.ddaModifier);
    }

    _isTimedMode() {
        return this.level.isEndless || this.level.isDaily;
    }

    _isLevelDone() {
        if (this._isTimedMode()) {
            return this.elapsedTime >= (this.level.timerSeconds || 90);
        }
        return isLevelComplete(this.elapsedTime);
    }

    _shouldSpawnTarget() {
        if (this.gameOver || this.paused) return false;
        if (this._isLevelDone()) return false;
        const timeSince = this.elapsedTime - this.lastSpawnTime;
        return timeSince >= this._getEffectiveSpawnRate() &&
               this.targets.length < this.level.maxSimultaneous;
    }

    _spawnTarget(vw, vh) {
        const area = this.level.spawnArea;
        const margin = (1 - area) / 2;
        const x = (margin + Math.random() * area) * vw;
        const y = (margin + Math.random() * area) * vh;

        // Determine target type (priority: decoy > golden > moving > standard)
        let type = TARGET_TYPE_STANDARD;
        const wave = getCurrentWave(this.elapsedTime);
        const roll = Math.random();

        if (wave.name !== 'warmup') {
            if (roll < this.level.decoyChance) {
                type = TARGET_TYPE_DECOY;
                this._decoySpawned = true;
            } else if (roll < this.level.decoyChance + this.level.goldenChance) {
                type = TARGET_TYPE_GOLDEN;
                this._goldenSpawned = true;
            } else if (roll < this.level.decoyChance + this.level.goldenChance + this.level.movingChance) {
                type = TARGET_TYPE_MOVING;
                this._movingSpawned = true;
            }
        }

        // Moving targets get velocity
        let vxTarget = 0, vyTarget = 0;
        if (type === TARGET_TYPE_MOVING) {
            const angle = Math.random() * Math.PI * 2;
            const speed = this.level.movingSpeed || 30;
            vxTarget = Math.cos(angle) * speed;
            vyTarget = Math.sin(angle) * speed;
        }

        const target = {
            id: Date.now() + Math.random(),
            type,
            x, y,
            vx: vxTarget,
            vy: vyTarget,
            spawnTime: this.elapsedTime,
            duration: this._getEffectiveDuration(),
            scale: 0,
            appearing: true,
            trail: type === TARGET_TYPE_MOVING ? [] : null,
        };

        this.targets.push(target);
        this.lastSpawnTime = this.elapsedTime;
    }

    // Update DDA — silent, invisible adjustment ±15%
    _updateDDA() {
        if (this.recentWindow.length < 15) return;
        const hits = this.recentWindow.filter(Boolean).length;
        const accuracy = hits / this.recentWindow.length;

        if (accuracy > 0.85) {
            this.ddaModifier = Math.min(1.15, this.ddaModifier + 0.02);
        } else if (accuracy < 0.55) {
            this.ddaModifier = Math.max(0.85, this.ddaModifier - 0.02);
        }
    }

    _recordHitMiss(isHit) {
        this.recentWindow.push(isHit);
        if (this.recentWindow.length > 15) {
            this.recentWindow.shift();
        }
        this._updateDDA();
    }

    update(dt, vw, vh) {
        if (this.paused || this.gameOver) return;

        this.elapsedTime += dt;

        // Check level complete
        if (this._isLevelDone() && this.targets.length === 0) {
            this.gameOver = true;
            gameAudio.levelComplete();
            return;
        }

        // Spawn targets
        if (this._shouldSpawnTarget()) {
            this._spawnTarget(vw, vh);
        }

        // Update targets
        for (let i = this.targets.length - 1; i >= 0; i--) {
            const t = this.targets[i];
            const age = this.elapsedTime - t.spawnTime;

            // Appear animation (200ms)
            if (t.appearing) {
                t.scale = Math.min(1, age / 0.2);
                if (age >= 0.2) {
                    t.appearing = false;
                    t.scale = 1;
                }
            }

            // Move moving targets
            if ((t.type === TARGET_TYPE_MOVING) && !t.appearing) {
                t.x += t.vx * dt;
                t.y += t.vy * dt;

                // Bounce off walls with padding
                const pad = 30;
                if (t.x < pad) { t.x = pad; t.vx = Math.abs(t.vx); }
                if (t.x > vw - pad) { t.x = vw - pad; t.vx = -Math.abs(t.vx); }
                if (t.y < pad) { t.y = pad; t.vy = Math.abs(t.vy); }
                if (t.y > vh - pad) { t.y = vh - pad; t.vy = -Math.abs(t.vy); }

                // Update trail
                if (t.trail) {
                    t.trail.push({ x: t.x, y: t.y, time: this.elapsedTime });
                    // Keep last 8 trail points
                    if (t.trail.length > 8) t.trail.shift();
                }
            }

            // Check expired
            if (age >= t.duration && !t.appearing) {
                // Decoys expiring is not a miss — player correctly avoided them
                if (t.type === TARGET_TYPE_DECOY) {
                    this.targets.splice(i, 1);
                    continue;
                }
                // Standard/golden/moving expiring is a miss
                this.totalMisses++;
                this.combo = 0;
                this._recordHitMiss(false);
                gameAudio.targetMiss();
                this.targets.splice(i, 1);
            }
        }

        // Update particles + popups
        updateParticles(this.particles, dt);
        updatePopups(this.popups, dt);

        // Screen flash decay
        if (this.screenFlash > 0) {
            this.screenFlash = Math.max(0, this.screenFlash - dt * 20);
        }
        if (this.comboMilestoneHit !== null) {
            this.comboMilestoneHit = null;
        }
    }

    handleTap(tapX, tapY, vw, vh) {
        if (this.paused || this.gameOver) return null;

        // Find closest target within hit zone
        const baseSize = this.getBaseTargetSize(vw, vh);
        let hitTarget = null;
        let hitDist = Infinity;

        for (const t of this.targets) {
            if (t.appearing) continue;
            const dx = tapX - t.x;
            const dy = tapY - t.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const hitZone = this.getHitZoneSize(baseSize) / 2;
            if (dist <= hitZone && dist < hitDist) {
                hitTarget = t;
                hitDist = dist;
            }
        }

        if (!hitTarget) return null; // tapped empty space — no penalty

        // Remove target
        this.targets = this.targets.filter(t => t.id !== hitTarget.id);

        if (hitTarget.type === TARGET_TYPE_DECOY) {
            return this._handleDecoyHit(hitTarget);
        }
        if (hitTarget.type === TARGET_TYPE_GOLDEN) {
            return this._handleGoldenHit(hitTarget, vw, vh);
        }
        return this._handleStandardHit(hitTarget, vw, vh);
    }

    _handleStandardHit(target, vw, vh) {
        const reactionTime = (this.elapsedTime - target.spawnTime) * 1000; // ms
        this.reactionTimes.push(reactionTime);
        this.totalHits++;
        this.combo++;
        this.tutorialHitCount++;

        if (this.combo > this.bestCombo) {
            this.bestCombo = this.combo;
        }
        this._recordHitMiss(true);

        // Speed bonus
        const reactionSec = reactionTime / 1000;
        let speedMult = 1.0;
        let speedText = null;
        let speedColor = '#FFFFFF';
        if (reactionSec < SPEED_THRESHOLDS.PERFECT) {
            speedMult = 3.0; speedText = 'PERFECT!'; speedColor = '#FFD700';
            gameAudio.perfectHit();
        } else if (reactionSec < SPEED_THRESHOLDS.FAST) {
            speedMult = 2.0; speedText = 'Fast!'; speedColor = '#4FC3F7';
        } else if (reactionSec < SPEED_THRESHOLDS.GOOD) {
            speedMult = 1.5; speedText = 'Good'; speedColor = '#66BB6A';
        }

        // Combo multiplier
        let comboMult = 1;
        for (const cm of COMBO_MULTIPLIERS) {
            if (this.combo >= cm.min) { comboMult = cm.mult; break; }
        }

        const points = Math.round(10 * speedMult * comboMult);
        this.score += points;

        // Sound
        gameAudio.targetHit(this.combo);

        // Particles
        const color = speedText === 'PERFECT!' ? '#FFD700' : '#00E5FF';
        const particleCount = speedText === 'PERFECT!' ? 18 : 12;
        spawnParticles(this.particles, target.x, target.y, particleCount, color);

        // Score popup
        this.popups.push(createPopup(target.x, target.y - 20, '+' + points, '#FFFFFF'));
        if (speedText) {
            this.popups.push(createPopup(target.x, target.y - 45, speedText, speedColor));
        }

        // Screen flash
        this.screenFlash = 0.03;

        // Combo milestones
        const milestone = COMBO_MILESTONES.find(m => this.combo === m);
        if (milestone) {
            const tier = COMBO_MILESTONES.indexOf(milestone);
            gameAudio.comboMilestone(tier);
            this.comboMilestoneHit = milestone;
            this.popups.push(createPopup(
                vw / 2, vh / 2 - 50,
                this.combo + 'x COMBO!',
                '#FFD700'
            ));
        }

        // Haptic (progressive enhancement)
        if (navigator.vibrate) {
            if (speedText === 'PERFECT!') {
                navigator.vibrate(15);
            } else {
                navigator.vibrate(8);
            }
        }

        return {
            type: 'hit',
            points,
            speedText,
            speedColor,
            reactionTime,
            combo: this.combo,
            comboMult,
        };
    }

    _handleDecoyHit(target) {
        this.totalDecoyTaps++;
        this.combo = 0;
        this.score = Math.max(0, this.score - 50);
        this._recordHitMiss(false);
        gameAudio.decoyTapped();

        // Red particles
        spawnParticles(this.particles, target.x, target.y, 8, '#FF3344');
        this.popups.push(createPopup(target.x, target.y - 20, '-50', '#FF3344'));

        // Haptic error buzz
        if (navigator.vibrate) {
            navigator.vibrate([30, 10, 50]);
        }

        return { type: 'decoy', points: -50 };
    }

    _handleGoldenHit(target, vw, vh) {
        const reactionTime = (this.elapsedTime - target.spawnTime) * 1000;
        this.reactionTimes.push(reactionTime);
        this.totalHits++;
        this.goldenHits++;
        this.combo++;
        this.tutorialHitCount++;

        if (this.combo > this.bestCombo) {
            this.bestCombo = this.combo;
        }
        this._recordHitMiss(true);

        // Golden targets give 5x base points
        let comboMult = 1;
        for (const cm of COMBO_MULTIPLIERS) {
            if (this.combo >= cm.min) { comboMult = cm.mult; break; }
        }
        const points = Math.round(50 * comboMult); // 5x base (10*5)
        this.score += points;

        // Sound — sparkle arpeggio
        gameAudio.goldenHit();

        // Gold particles — extra big burst
        spawnParticles(this.particles, target.x, target.y, 20, '#FFD700');

        // Popups
        this.popups.push(createPopup(target.x, target.y - 20, '+' + points, '#FFD700'));
        this.popups.push(createPopup(target.x, target.y - 50, 'GOLDEN!', '#FFD700'));

        // Big screen flash
        this.screenFlash = 0.06;

        // Combo milestone check
        const milestone = COMBO_MILESTONES.find(m => this.combo === m);
        if (milestone) {
            const tier = COMBO_MILESTONES.indexOf(milestone);
            gameAudio.comboMilestone(tier);
            this.comboMilestoneHit = milestone;
            this.popups.push(createPopup(vw / 2, vh / 2 - 50, this.combo + 'x COMBO!', '#FFD700'));
        }

        // Haptic — juicy
        if (navigator.vibrate) {
            navigator.vibrate([10, 30, 10]);
        }

        return { type: 'golden', points, combo: this.combo, comboMult, reactionTime };
    }

    getStats() {
        const totalTargets = this.totalHits + this.totalMisses;
        const accuracy = totalTargets > 0 ? this.totalHits / totalTargets : 0;
        const avgReaction = this.reactionTimes.length > 0
            ? this.reactionTimes.reduce((a, b) => a + b, 0) / this.reactionTimes.length
            : null;

        return {
            score: this.score,
            accuracy,
            targetsHit: this.totalHits,
            targetsMissed: this.totalMisses,
            totalTargets,
            bestCombo: this.bestCombo,
            avgReactionTime: avgReaction ? Math.round(avgReaction) : null,
            bestReactionTime: this.reactionTimes.length > 0
                ? Math.round(Math.min(...this.reactionTimes))
                : null,
            decoyTaps: this.totalDecoyTaps,
            goldenHits: this.goldenHits,
        };
    }

    getProgress() {
        const total = this._isTimedMode()
            ? (this.level.timerSeconds || 90)
            : TOTAL_GAMEPLAY_TIME;
        return {
            elapsed: this.elapsedTime,
            total,
            wave: this._isTimedMode() ? null : getCurrentWave(this.elapsedTime),
            timeRemaining: this._isTimedMode() ? Math.max(0, total - this.elapsedTime) : null,
            isComplete: this.gameOver,
        };
    }
}

export default GameEngine;
export { TARGET_TYPE_STANDARD, TARGET_TYPE_DECOY, TARGET_TYPE_GOLDEN, TARGET_TYPE_MOVING };
